import {
    ColorNeutralGrey3,
    ColorNeutralWhite,
    FontSize10,
    Spacing12,
    Spacing2,
    Spacing4,
    Spacing8
} from 'autogen/design-tokens/tokens';
import styled, { css } from 'styled-components';
import type { HTMLAttributes } from 'react';
import DiagonalLines from './DiagonalLines';
import type { SwatchRendererType } from './shared/types';
import { DisplayTypeSize, selectedStyling, swatchStateStyling } from './shared/styles';
import LinkAction from 'components/shared/Routing/components/LinkAction/LinkAction';

type TextSwatchProps = Pick<SwatchRendererType, 'unavailable' | 'isSelected' | 'size' | 'name' | 'linkAction'> &
    HTMLAttributes<HTMLSpanElement>;

// #region Styles
const xsStyling = css`
    font-size: ${FontSize10};
    padding: 0 ${Spacing4};
`;

const smStyling = css`
    ${xsStyling}

    padding: ${Spacing2} ${Spacing4};
`;

const lgStyling = css`
    height: ${DisplayTypeSize.Text.height};
    min-width: ${DisplayTypeSize.Text.width};
    padding: ${Spacing12} ${Spacing8};
`;

const sizeStyling = {
    xs: xsStyling,
    sm: smStyling,
    lg: lgStyling
};

const baseWrapperStyles = css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    text-overflow: ellipsis;
    overflow: hidden;
    outline: 1px solid ${ColorNeutralGrey3};
    outline-offset: -1px;
    white-space: nowrap;
    text-align: center;
    background-color: ${ColorNeutralWhite};
`;

const LinkWrapper = styled(LinkAction)<{ $isSelected: boolean; $size: TextSwatchProps['size']; $isLink: boolean }>`
    ${baseWrapperStyles}

    ${({ $size }) => $size && sizeStyling[$size]}

    ${({ $isSelected, $size }) => $isSelected && selectedStyling({ $size })}
    ${({ $size, $isLink }) => $isLink && swatchStateStyling({ $size })}
`;

const Wrapper = styled.div<{ $isSelected: boolean; $size: TextSwatchProps['size'] }>`
    ${baseWrapperStyles}

    ${({ $size }) => $size && sizeStyling[$size]}

    ${({ $isSelected, $size }) => $isSelected && selectedStyling({ $size })}
`;

const EllipsesContainer = styled.div`
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
`;

// #endregion Styles

export default function TextSwatch({
    name,
    isSelected,
    unavailable,
    size,
    linkAction,
    onClick,
    ...restProps
}: TextSwatchProps) {
    if (onClick) {
        return (
            <Wrapper $isSelected={isSelected} $size={size} onClick={onClick} as="button" {...restProps}>
                <EllipsesContainer>{name}</EllipsesContainer>
                {unavailable && <DiagonalLines size={size} />}
            </Wrapper>
        );
    }

    if (!linkAction) {
        return (
            <Wrapper $isSelected={isSelected} $size={size} {...restProps}>
                <EllipsesContainer>{name}</EllipsesContainer>
                {unavailable && <DiagonalLines size={size} />}
            </Wrapper>
        );
    }

    return (
        <LinkWrapper action={linkAction} $isLink={!!linkAction} $isSelected={isSelected} $size={size} {...restProps}>
            <EllipsesContainer>{name}</EllipsesContainer>
            {unavailable && <DiagonalLines size={size} />}
        </LinkWrapper>
    );
}
